import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import ReactTable from "../../../shared/components/tables/ReactTable";

import { DeleteRowIcon, EditCountryIcon, ViewIcon } from "../../../assets";
import {
  SCREEN_PATH,
  EMPTY_STRING,
  PAYLOAD_KEYS,
  adminReduxConstants,
} from "../../../constants";
import {
  getSortBy,
  getPageNumber,
  buildFilterQuery,
  customStringSorting,
} from "../../../shared/utility";
import SearchField from "../../../shared/components/search/SearchField";
import MainSpinner from "../../../shared/components/spinner/MainSpinner";
import RoundedButton from "../../../shared/components/buttons/RoundedButton";
import ConfirmationPopup from "../../../shared/components/modals/ConfirmationPopup";
import MainContentWrapper from "../../../shared/components/main-content-wrapper/MainContentWrapper";
import { adminActions } from "../../../redux/actions/admin.actions";

const { FILTER_QUERY, PAGE_SIZE, PAGE_NUMBER, SEARCH } = PAYLOAD_KEYS;
const { GET_COUNTRIES_SETTINGS, COUNTRIES_SETTINGS_PAGINATION } =
  adminReduxConstants;

const CountrySettings = ({
  spinnerActions,
  countrySettingsPagination,
  updatePagination,
  countriesSettings,
  getCountriesSettings,
  deleteCountrySetting,
}) => {
  const navigate = useNavigate();
  const navigateToDetails = (id, edit = false) => {
    navigate(SCREEN_PATH.ADMIN_COUNTRIES_DETAILS.replace(":id", id), {
      state: { isEditing: edit },
    });
  };

  const [sorting, setSorting] = useState({ id: "id", desc: true });
  const [deleteCountryConfirmation, setDeleteCountryConfirmation] =
    useState(false);
  const [deleteCountryId, setDeleteCountryId] = useState(null);

  const openConfirmationModal = () => setDeleteCountryConfirmation(true);
  const closeConfirmationModal = () => {
    setDeleteCountryConfirmation(false);
    setDeleteCountryId(null);
  };

  const onConfirmDelete = () => {
    deleteCountrySetting(deleteCountryId, () => {
      getCountriesSettings(countrySettingsPagination);
      closeConfirmationModal();
    });
  };

  const spinner = useCallback(
    (type) => spinnerActions.findIndex((s) => s === type),
    [spinnerActions]
  );

  const columnHelper = createColumnHelper();
  const columns = [
    columnHelper.accessor("country.name", {
      header: <div className="column-head">Country Name</div>,
      cell: ({ row: { original } }) => <div>{original.country?.name}</div>,
      size: 40,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),
    columnHelper.accessor("country.nationality", {
      header: <div className="column-head">Nationality</div>,
      cell: ({ row: { original } }) => (
        <div>{original.country.nationality}</div>
      ),
      size: 40,
      enableSorting: true,
      sortingFn: customStringSorting,
    }),

    columnHelper.accessor("action", {
      header: <div className="column-head text-end">Action</div>,
      enableSorting: false,
      cell: ({ row: { original } }) => (
        <div className=" d-flex justify-content-end gap-2">
          <img
            className="pointer"
            src={EditCountryIcon}
            onClick={() => navigateToDetails(original.id, true)}
            alt={EMPTY_STRING}
          />
          <img
            className="pointer"
            src={ViewIcon}
            onClick={() => navigateToDetails(original.id)}
            alt={EMPTY_STRING}
          />
          <img
            className="pointer"
            src={DeleteRowIcon}
            onClick={() => {
              openConfirmationModal();
              setDeleteCountryId(original.id);
            }}
            alt={EMPTY_STRING}
          />
        </div>
      ),
    }),
  ];

  const getFilterQuery = (data) => {
    const searchFields = ["Country.Name", "Country.Nationality"];
    return buildFilterQuery(data, searchFields);
  };

  const handleUpdatePagination = (data, type) => {
    const payload = {
      ...countrySettingsPagination,
      [SEARCH]: type === SEARCH ? data : countrySettingsPagination.search,
      [PAGE_SIZE]:
        type === PAGE_SIZE ? data : countrySettingsPagination.pageSize,
      [PAGE_NUMBER]: getPageNumber(type, data, countrySettingsPagination),
    };

    updatePagination(payload, COUNTRIES_SETTINGS_PAGINATION);
  };

  useEffect(() => {
    return () => {
      updatePagination(
        {
          [PAGE_SIZE]: 10,
          [PAGE_NUMBER]: 0,
          [SEARCH]: EMPTY_STRING,
          [FILTER_QUERY]: EMPTY_STRING,
        },
        COUNTRIES_SETTINGS_PAGINATION
      );
    };
  }, []);

  useEffect(() => {
    const updatedPayload = {
      ...countrySettingsPagination,
      [FILTER_QUERY]: getFilterQuery(countrySettingsPagination.search),
      orderBy: getSortBy(sorting),
    };

    getCountriesSettings(updatedPayload);
    // eslint-disable-next-line
  }, [
    countrySettingsPagination.search,
    countrySettingsPagination.pageNumber,
    countrySettingsPagination.pageSize,
    sorting,
  ]);

  return (
    <MainContentWrapper>
      {spinner(GET_COUNTRIES_SETTINGS) > -1 && (
        <MainSpinner className="admin-spinner" />
      )}
      <ConfirmationPopup
        message={"Are you sure you want to delete this country?"}
        show={deleteCountryConfirmation}
        onCancel={closeConfirmationModal}
        onConfirm={onConfirmDelete}
      />
      <div className="admin-page-heading">Countries Information</div>
      <div className="d-flex justify-content-between align-items-center flex-wrap">
        <SearchField
          value={countrySettingsPagination.search}
          handleSearch={(val) => handleUpdatePagination(val, SEARCH)}
        />
        <div className="custom-button">
          <RoundedButton
            className="add-admin-button mt-4 "
            onClick={() => navigate(SCREEN_PATH.ADMIN_COUNTRIES_ADD)}
          >
            Add New Country
          </RoundedButton>
        </div>
      </div>
      <ReactTable
        data={countriesSettings}
        columns={columns}
        paginationData={{
          currentPage: countrySettingsPagination.pageNumber,
          totalItems: countrySettingsPagination?.total ?? 0,
          pageSize: countrySettingsPagination.pageSize,
          onPageChange: (page) => handleUpdatePagination(page, PAGE_NUMBER),
          onPageSizeChange: (pageSize) =>
            handleUpdatePagination(pageSize, PAGE_SIZE),
        }}
        sortedBy={sorting}
        onColumnSort={(sortBy) => setSorting(sortBy)}
      />
    </MainContentWrapper>
  );
};

const mapStateToProps = (state) => ({
  spinnerActions: state.systemReducer.spinnerActions,
  countrySettingsPagination: state.adminReducer.countrySettingsPagination,
  countriesSettings: state.adminReducer.countriesSettings,
});

const mapDispatchToProps = (dispatch) => ({
  getCountriesSettings: (data) =>
    dispatch(adminActions.getCountriesSettings(data)),
  updatePagination: (data, type) =>
    dispatch(adminActions.updatePagination(data, type)),
  deleteCountrySetting: (id, func) =>
    dispatch(adminActions.deleteCountrySetting(id, func)),
});

export default connect(mapStateToProps, mapDispatchToProps)(CountrySettings);
